/**
 * Enrollment lookup, cancel resolution, booked-row display, and stale-selection helpers for {@link MeetingSelector}.
 */
import type { AvailableMeeting, QueryParams } from '@/types/remote/session.types';
import { toCalendarDateString } from '@/utils/format-date-utils';

/** Meeting types the onboarding selector can book. */
export type SelectableMeetingType =
  | 'information_meeting'
  | 'information_meeting_in_person'
  | 'welcome_meeting';

/** GET /sessions params for the RI picker. RA uses the dedicated onboarding list. */
export function buildAvailableMeetingsQueryParams(
  type: Exclude<SelectableMeetingType, 'welcome_meeting'>,
): QueryParams {
  return {
    dateFrom: new Date(),
    statuses: ['planned'],
    types: [type],
  };
}

/** Participant row we can resolve an enrollment id from. */
export interface MeetingParticipantRef {
  id: string;
  userId?: string;
  user?: { id?: string } | null;
}

/** List/detail session slice needed to build an {@link AvailableMeeting}. */
export interface MeetingSessionSource {
  id: string;
  sessionDate: Date | string;
  startTime: string;
  endTime?: string | null;
  availableSlots?: number;
  type: string;
  participants?: MeetingParticipantRef[];
}

/** Inputs used to resolve the participant row to delete on cancel. */
export interface CancelMeetingEnrollmentInput {
  mode: 'create' | 'edit';
  knownParticipantId: string | null;
  enrolledParticipantId?: string | null;
}

/** Create-mode placeholder stored after a local join (not a real participant row). */
const PENDING_PARTICIPANT_ID = 'pending';

/** Find the session participant row for this user, if they are enrolled. */
export function findEnrolledParticipantId(
  participants: MeetingParticipantRef[] | null | undefined,
  userId: string,
): string | null {
  if (!userId) return null;
  const match = participants?.find((row) => row.userId === userId || row.user?.id === userId);
  return match?.id ?? null;
}

/** Map a list/detail session to the selector row, including this user's enrollment. */
export function toAvailableMeeting(
  session: MeetingSessionSource,
  userId: string,
): AvailableMeeting {
  return {
    id: session.id,
    sessionDate: toCalendarDateString(session.sessionDate),
    startTime: session.startTime,
    endTime: session.endTime ?? '',
    availableSlots: session.availableSlots,
    type: session.type,
    enrolledParticipantId: findEnrolledParticipantId(session.participants, userId),
  };
}

/** True when the meeting's local `sessionDate` + `startTime` is already in the past. */
export function isMeetingInThePast(meeting: AvailableMeeting, now = new Date()): boolean {
  return new Date(`${meeting.sessionDate}T${meeting.startTime}`) < now;
}

/** Real participant id — ignore the create-mode `pending` placeholder. */
export function resolveCancelParticipantId(
  joinedParticipantId: string | null,
  enrolledParticipantId: string | null | undefined,
): string | null {
  const fromJoin =
    joinedParticipantId && joinedParticipantId !== PENDING_PARTICIPANT_ID
      ? joinedParticipantId
      : null;
  return fromJoin ?? enrolledParticipantId ?? null;
}

/** Participant id to DELETE, or null when cancel is local-only (create mode). */
export function resolveParticipantIdForCancel(input: CancelMeetingEnrollmentInput): string | null {
  if (input.mode === 'create') return null;
  return resolveCancelParticipantId(input.knownParticipantId, input.enrolledParticipantId);
}

/** Edit cancel: roster GET first, then in-memory join/list ids. */
export function resolveEditCancelParticipantId(params: {
  userId: string;
  knownParticipantId: string | null;
  enrolledParticipantId?: string | null;
  participants: MeetingParticipantRef[] | null | undefined;
}): string | null {
  return (
    findEnrolledParticipantId(params.participants, params.userId) ??
    resolveCancelParticipantId(params.knownParticipantId, params.enrolledParticipantId)
  );
}

/** Planned meeting matching `value`, or null when the id is missing or no longer listed. */
export function findSelectedMeeting<T extends { id: string }>(
  value: string | null | undefined,
  meetings: T[],
): T | null {
  if (!value) return null;
  return meetings.find((meeting) => meeting.id === value) ?? null;
}

/** True when the form still holds a sessionId that is not in the planned list (cancelled, past). */
export function isOrphanMeetingSelection(
  value: string | null | undefined,
  meetings: { id: string }[],
): boolean {
  return Boolean(value) && findSelectedMeeting(value, meetings) === null;
}

/** Prefer the bookable-list row, else GET /:id, else the last row already shown for this id. */
export function resolveDisplayedMeeting<T extends { id: string }>(
  value: string | null | undefined,
  meetings: T[],
  bookedMeeting: T | null | undefined,
  heldMeeting?: T | null,
): T | null {
  const fromList = findSelectedMeeting(value, meetings);
  if (fromList) return fromList;
  if (value && bookedMeeting?.id === value) return bookedMeeting;
  if (value && heldMeeting?.id === value) return heldMeeting;
  return null;
}

/** RA list drops a full slot — do not clear the booking until GET /sessions/:id fails. */
export function shouldClearOrphanMeetingSelection(params: {
  value: string | null | undefined;
  meetings: { id: string }[];
  bookedMeetingId?: string | null;
  bookedLookupSettled: boolean;
}): boolean {
  if (!isOrphanMeetingSelection(params.value, params.meetings)) return false;
  if (params.bookedMeetingId && params.bookedMeetingId === params.value) return false;
  return params.bookedLookupSettled;
}

/** Form bag that may still hold a locally cancelled meeting booking. */
export type MeetingBookingFormSlice = {
  sessionId?: unknown;
};

/** Keep a local cancel or a newly picked booking when GET hydrate is stale. */
export function preserveClearedMeetingSessionId<T extends MeetingBookingFormSlice>(
  prev: T,
  incoming: T,
): T {
  if (prev.sessionId === incoming.sessionId) return incoming;
  if (prev.sessionId === null && incoming.sessionId) {
    return { ...incoming, sessionId: null };
  }
  if (typeof prev.sessionId === 'string' && prev.sessionId.length > 0) {
    return { ...incoming, sessionId: prev.sessionId };
  }
  return incoming;
}
